import { create } from 'zustand';
import type { components } from '@/api/generated/schema';

export type CurrentUser =
  components['schemas']['PixlideApplicationFeaturesAuthenticationCurrentUserResponse'];

interface AuthState {
  /**
   * Access token, conservé EN MÉMOIRE UNIQUEMENT.
   *
   * Même contrat de sécurité que `frontend/` (voir son `stores/auth-store.ts`) : ni
   * `localStorage`, ni `sessionStorage`. Le refresh token vit dans un cookie `HttpOnly`
   * posé par l'API, invisible au JavaScript.
   */
  accessToken: string | null;
  user: CurrentUser | null;

  /** `false` jusqu'à la première tentative de restauration de session. */
  isBootstrapped: boolean;

  setSession: (accessToken: string) => void;
  setUser: (user: CurrentUser | null) => void;
  markBootstrapped: () => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  user: null,
  isBootstrapped: false,

  setSession: (accessToken) => set({ accessToken }),
  setUser: (user) => set({ user }),
  markBootstrapped: () => set({ isBootstrapped: true }),
  clear: () => set({ accessToken: null, user: null }),
}));

/**
 * Accès hors composant React, pour le client HTTP.
 *
 * Le client d'API n'est pas un composant : il ne peut pas utiliser de hook. Il lit donc
 * l'état directement via `getState()`, ce qui est le mode d'emploi prévu par Zustand.
 */
export const authStore = {
  getAccessToken: () => useAuthStore.getState().accessToken,
  setSession: (accessToken: string) => useAuthStore.getState().setSession(accessToken),
  clear: () => useAuthStore.getState().clear(),
};

/**
 * Propagation de la déconnexion aux autres onglets du back-office.
 *
 * Chaque onglet garde son propre access token en mémoire : sans ce canal, se déconnecter
 * dans un onglet laissait les autres naviguer jusqu'à l'expiration du jeton. Seule la
 * déconnexion VOLONTAIRE est diffusée, comme côté `frontend/`.
 */
const logoutChannel =
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('pixlide-backoffice-auth');

logoutChannel?.addEventListener('message', (event: MessageEvent) => {
  if (event.data === 'logout') {
    useAuthStore.getState().clear();
  }
});

export function broadcastLogout(): void {
  logoutChannel?.postMessage('logout');
}

/**
 * Permission des super administrateurs : suppression de comptes, création et promotion
 * d'administrateurs. Masquer les actions sans elle n'est qu'un confort — l'API les refuse
 * de toute façon en 403.
 */
export const SUPER_ADMIN_PERMISSION = 'platform:super-admin';

/** Vrai si l'utilisateur détient la permission demandée. */
export function useHasPermission(permission: string): boolean {
  return useAuthStore((state) => state.user?.permissions.includes(permission) ?? false);
}
